import { ListPagination } from "../../conponents/Pagination";
import { useLocale } from "../../../hooks/useLocale";
import { AdminButton, AdminInput, AdminSelect } from "./AdminControls";
import { useEffect, useState, type FormEvent } from "react";
import { RotateCcw, RotateCw, Search, SlidersHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GD } from "src/GD";
import { OVERVIEW_ANALYSIS_USER_IDS } from "src/permissions";
import { BankAnalysisPopup } from "src/view/statView/BankAnalysisPopup";
import { getRankingAnalysisEntity, type BankAnalysisRequest } from "src/view/statView/overviewBankAnalysis";
import { RuleBlockDetailsPopup } from "./RuleBlockDetailsPopup";
import { RuleBlocksTable } from "./RuleBlocksTable";
import { RULE_ACTIONS, RULE_OUTCOMES, defaultRuleFilters, getRuleBlocksRequest, parseRuleBlocks, ruleAnalysisPeriod, type RuleFilters, type RuleList, type RuleRecord } from "./ruleBlocksData";

const Panel = styled.section`
	min-width: 0; border: 1px solid #e1e6ef; border-radius: 12px; background: #fff; color: #344054;
	font: 13px/1.5 system-ui, sans-serif;
	form { padding: 12px 16px; border-bottom: 1px solid #edf0f5; }
	fieldset { min-width: 0; padding: 0; border: 0; margin: 0; }
	.filter-row { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 10px; }
	.date-range { display: flex; flex: 0 1 310px; gap: 8px; min-width: 0; }
	.date-range label { flex: 1; }
	.filter-row > .field-rule_name { flex: 1 1 180px; max-width: 360px; }
	.filter-row > .field-outcome { flex: 0 1 160px; }
	.filters { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr)); gap: 10px; }
	.filters-more { margin-top: 12px; padding-top: 12px; border-top: 1px solid #edf0f5; }
	label { display: flex; flex-direction: column; gap: 4px; min-width: 0; color: #667085; font-size: 12px; }
	input { width: 100%; min-width: 0; }
	button:disabled { cursor: default; }
	summary { cursor: pointer; color: #167ca6; width: fit-content; }
	.actions, .toolbar, footer { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
	.actions { gap: 6px; }
	.actions button { white-space: nowrap; }
	.actions .reset { width: 36px; padding: 0; }
	.actions button[aria-expanded="true"] { color: #167ca6; background: #edf7fb; border-color: #c5e4f0; }
	.filter-count { min-width: 18px; padding: 0 4px; border-radius: 4px; background: #edf7fb; color: #167ca6; font-size: 11px; }
	.validation { margin: 8px 0 0; }
	.toolbar { justify-content: space-between; padding: 12px 16px; }
	h3 { margin: 0; font-size: 14px; font-weight: 600; }
	.table-scroll { overflow-x: auto; max-width: 100%; }
	table { width: 100%; min-width: 880px; border-collapse: collapse; text-align: left; }
	th, td { padding: 10px 14px; border-bottom: 1px solid #edf0f5; vertical-align: top; }
	th { background: #f8fafc; color: #667085; font-weight: 500; white-space: nowrap; }
	td { font-variant-numeric: tabular-nums; }
	tbody tr { cursor: pointer; }
	tbody tr:hover { background: #fafbfc; }
	td small { display: block; margin-top: 4px; color: #667085; font-size: 11px; }
	.date-cell, .context-cell, .result-cell { white-space: nowrap; }
	.rule-cell { min-width: 160px; max-width: 300px; overflow-wrap: anywhere; font-weight: 500; }
	.entity-cell { min-width: 200px; max-width: 340px; overflow-wrap: anywhere; }
	.entity-cell .email-link { display: inline; min-height: 0; padding: 0; border: 0; border-radius: 2px; background: transparent; color: #167ca6; text-align: left; overflow-wrap: anywhere; }
	.entity-cell .email-link:hover { background: transparent; text-decoration: underline; text-underline-offset: 3px; }
	.open-cell { width: 36px; padding-left: 0; }
	.open-record { width: 32px; min-height: 32px; padding: 0; }
	.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0; }
	.badge { display: inline-block; padding: 2px 7px; border-radius: 5px; background: #f2f4f7; white-space: nowrap; }
	.badge[data-outcome="blocked"], .badge[data-outcome="check_failed"] { background: #fdf0f2; color: #b42342; }
	.badge[data-outcome="allowed"] { background: #edf8f2; color: #18774d; }
	.badge[data-outcome="warned"] { background: #fff7e7; color: #926000; }
	.state { padding: 32px 16px; text-align: center; color: #667085; }
	.error { color: #b42342; }
	footer { justify-content: space-between; padding: 12px 16px; }
	.paging { display: flex; align-items: center; gap: 12px; }
	footer label { flex-direction: row; align-items: center; }
	footer select { width: auto; }
	&[data-embedded="true"] { margin-top: 0; border: 0; border-radius: 0; }
	&[data-embedded="true"] form, &[data-embedded="true"] .toolbar, &[data-embedded="true"] footer { padding-inline: 0; }
	&[data-embedded="true"] table { min-width: 620px; }
	@media (max-width: 600px) {
		.date-range { flex-basis: 100%; }
		.filter-row > .field-rule_name { max-width: none; flex-basis: 150px; }
		.filter-row > .field-outcome { flex: 1 1 130px; }
		.actions { flex-basis: 100%; }
	}
`;

export type RuleBlocksScope = { entity_type: "email"; entity_value: string; from: string; to: string };

export const RuleBlocksTab = ({ projectId, userId, scope }: { projectId: number; userId: number; scope?: RuleBlocksScope }) => {
	useLocale();
	const { t, i18n } = useTranslation();
	const [draft, setDraft] = useState(defaultRuleFilters);
	const [query, setQuery] = useState(() => ({ filters: defaultRuleFilters(), page: 0, perPage: 25, attempt: 0 }));
	const [validation, setValidation] = useState<string | null>(null);
	const [filtersExpanded, setFiltersExpanded] = useState(false);
	const [state, setState] = useState<{ query: typeof query; busy: boolean; data?: RuleList; error?: string; errorKey?: string }>({ query, busy: true });
	const [selection, setSelection] = useState<{ query: typeof query; record?: RuleRecord; analysis?: BankAnalysisRequest } | null>(null);
	const busy = state.query !== query || state.busy;
	const data = state.query === query ? state.data : undefined;

	useEffect(() => {
		const controller = new AbortController();
		setState({ query, busy: true });
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => { controller.abort(); setSelection(null); setState({ query, busy: false }); });
		const load = async () => {
			try {
				const filters = scope ? { ...query.filters, ...scope, outcome: "blocked" } : query.filters;
				const response = await GD.REQ_API.request({ ...getRuleBlocksRequest(projectId, filters, query.page, query.perPage), signal: controller.signal });
				if (controller.signal.aborted) return;
				if (response.error) { setState({ query, busy: false, error: response.error.message }); return; }
				const parsed = parseRuleBlocks(response.data);
				if (scope && parsed?.rows.some(row => row.entity_type !== scope.entity_type || row.entity_value !== scope.entity_value.trim() || row.outcome !== "blocked")) {
					setState({ query, busy: false, errorKey: "unrecognized" }); return;
				}
				setState(parsed ? { query, busy: false, data: parsed } : { query, busy: false, errorKey: "unrecognized" });
			} catch {
				if (!controller.signal.aborted) setState({ query, busy: false, errorKey: "loadError" });
			}
		};
		void load();
		return () => { controller.abort(); GD.S_AUTH_LOG_OUT.unsubscribe(logout); };
	}, [projectId, userId, query, scope?.entity_type, scope?.entity_value, scope?.from, scope?.to]);

	const apply = (event: FormEvent) => {
		event.preventDefault();
		try {
			getRuleBlocksRequest(projectId, draft, 0, query.perPage);
			setValidation(null);
			setQuery(current => ({ ...current, filters: { ...draft }, page: 0 }));
		} catch (error) { setValidation(error instanceof Error ? error.message : "invalidFilters"); }
	};
	const input = (field: keyof RuleFilters, type = "text") => <label key={field} className={`field-${field}`}>{t(`administration.fields.${field}`)}
		<AdminInput type={type} value={draft[field]} min={type === "number" ? 0 : undefined} step={type === "number" ? 1 : undefined} onChange={event => setDraft(current => ({ ...current, [field]: event.target.value }))} />
	</label>;
	const select = (field: "action" | "outcome" | "sandbox", values: readonly string[]) => <label className={`field-${field}`}>{t(`administration.fields.${field}`)}
		<AdminSelect value={draft[field]} onChange={event => setDraft(current => ({ ...current, [field]: event.target.value }))}>
			<option value="">{t("administration.all")}</option>
			{values.map(value => <option key={value} value={value}>{field === "sandbox" ? value : t(`administration.${field}.${value}`)}</option>)}
		</AdminSelect>
	</label>;
	const getEmailAction = (row: RuleRecord) => {
		if (scope || busy || !OVERVIEW_ANALYSIS_USER_IDS.includes(userId) || typeof row.entity_type !== "string" || row.entity_type.toLowerCase() !== "email" || typeof row.entity_value !== "string") return undefined;
		const bank = getRankingAnalysisEntity("Top emails", row.entity_value.trim(), i18n.language);
		const period = ruleAnalysisPeriod(row, query.filters);
		if (!bank || !period) return undefined;
		return {
			label: t("administration.emailAnalysis", { email: bank.label, from: period.from, to: period.to }),
			onClick: () => setSelection({ query, analysis: { bank, ...period, currency: "", userId, projectId } }),
		};
	};
	const extraFilterCount = (["id", "client_id", "mid", "reference_id", "entity_type", "entity_value", "action", "sandbox"] as const).filter(field => draft[field].trim() !== "").length;
	const canNext = !!data && (data.totalPages !== null ? query.page + 1 < data.totalPages : data.total !== null ? (query.page + 1) * query.perPage < data.total : data.rows.length >= query.perPage);

	return <><Panel aria-label={t("administration.blocks")} data-embedded={scope ? "true" : undefined}>
		<form onSubmit={apply}>
			<fieldset disabled={busy}>
				<div className="filter-row">
					{!scope && <div className="date-range">{input("from", "date")}{input("to", "date")}</div>}
					{input("rule_name")}{!scope && select("outcome", RULE_OUTCOMES)}
					<div className="actions">
						{!scope && <AdminButton variant="secondarySmall" size="small" type="button" aria-expanded={filtersExpanded} aria-controls="administration-rule-filters" onClick={() => setFiltersExpanded(value => !value)}><SlidersHorizontal size={15} aria-hidden="true" />{t("administration.moreFilters")}{extraFilterCount > 0 && <span className="filter-count">{extraFilterCount}</span>}</AdminButton>}
						<AdminButton variant="primarySmall" size="small" type="submit" className="primary"><Search size={15} aria-hidden="true" />{t("administration.apply")}</AdminButton>
						<AdminButton variant="secondarySmall" size="small" type="button" className="reset" title={t("administration.reset")} aria-label={t("administration.reset")} onClick={() => { const filters = defaultRuleFilters(); setDraft(filters); setValidation(null); setQuery(current => ({ ...current, filters, page: 0 })); }}><RotateCcw size={15} aria-hidden="true" /></AdminButton>
					</div>
				</div>
				{!scope && <div id="administration-rule-filters" className="filters-more" hidden={!filtersExpanded}>
					<div className="filters">{input("id", "number")}{input("client_id", "number")}{input("mid", "number")}{input("reference_id")}{input("entity_type")}{input("entity_value")}{select("action", RULE_ACTIONS)}{select("sandbox", ["0", "1", "2", "3"])}</div>
				</div>}
				{validation && <p className="validation error" role="alert">{t(`administration.${validation}`)}</p>}
			</fieldset>
		</form>
		<div className="toolbar"><h3>{t("administration.results")}{data?.total !== null && data?.total !== undefined ? ` · ${data.total.toLocaleString()}` : ""}</h3>
			<AdminButton variant="secondarySmall" type="button" disabled={busy} onClick={() => setQuery(current => ({ ...current, attempt: current.attempt + 1 }))}><RotateCw size={15} aria-hidden="true" />{t("common.refresh")}</AdminButton>
		</div>
		<div aria-busy={busy}>
			{busy ? <div className="state" role="status">{t("common.loading")}</div> : state.error || state.errorKey ? <div className="state error" role="alert">{state.error || t(`administration.${state.errorKey}`)}</div> : data?.rows.length === 0 ? <div className="state" role="status">{t(scope && !query.filters.rule_name.trim() ? "bankAnalysis.rulesEmpty" : "administration.empty")}</div> : data && <RuleBlocksTable rows={data.rows} onDetails={record => setSelection({ query, record })} getEmailAction={getEmailAction} hideEntity={!!scope} />}
		</div>
		<footer>
			<label>{t("administration.perPage")}<AdminSelect value={query.perPage} disabled={busy} onChange={event => setQuery(current => ({ ...current, perPage: Number(event.target.value), page: 0 }))}>{[25, 50, 100].map(value => <option key={value} value={value}>{value}</option>)}</AdminSelect></label>
			<ListPagination page={query.page} perPage={query.perPage} total={data?.total} totalPages={data?.totalPages} hasNext={canNext} disabled={busy} onPageChange={page => setQuery(current => ({ ...current, page }))} />
		</footer>
	</Panel>
		{!busy && selection?.query === query && selection.record && <RuleBlockDetailsPopup record={selection.record} projectId={projectId} userId={userId} onClose={() => setSelection(null)} />}
		{!busy && selection?.query === query && selection.analysis && OVERVIEW_ANALYSIS_USER_IDS.includes(userId) && <BankAnalysisPopup request={selection.analysis} onClose={() => setSelection(null)} onEntity={bank => setSelection(current => current?.analysis ? { ...current, analysis: { ...current.analysis, bank } } : null)} />}
	</>;
};
